/**
 * KEYPAIR CRYPTO MODULE
 * Implements X25519 key exchange for secure DEK sharing between users.
 * Uses tweetnacl for X25519 operations (curve25519-xsalsa20-poly1305).
 */

import nacl from "tweetnacl";
import { t } from "../../shared/i18n/index.js";

async function loadNacl() {
  return nacl;
}

export const Keypair = {
  /**
   * Generate a new X25519 keypair for key exchange
   * @returns {Promise<{publicKey: Uint8Array, secretKey: Uint8Array}>}
   */
  async generate() {
    const nacl = await loadNacl();
    return nacl.box.keyPair();
  },

  /**
   * Generate a keypair from a seed (deterministic)
   * @param {Uint8Array} seed - 32-byte seed
   * @returns {Promise<{publicKey: Uint8Array, secretKey: Uint8Array}>}
   */
  async fromSeed(seed) {
    const nacl = await loadNacl();
    return nacl.box.keyPair.fromSecretKey(seed);
  },

  /**
   * Encrypt a DEK for a recipient using their public key
   * Uses X25519 + XSalsa20-Poly1305 (nacl.box)
   *
   * @param {Uint8Array} dek - The document encryption key to share
   * @param {Uint8Array} recipientPublicKey - Recipient's X25519 public key
   * @param {Uint8Array} senderSecretKey - Sender's X25519 secret key
   * @returns {Promise<Uint8Array>} - Nonce + ciphertext (24 + N bytes)
   */
  async wrapDEK(dek, recipientPublicKey, senderSecretKey) {
    const nacl = await loadNacl();

    // Generate random nonce (24 bytes for XSalsa20)
    const nonce = nacl.randomBytes(24);

    // Encrypt using nacl.box (X25519 + XSalsa20-Poly1305)
    const ciphertext = nacl.box(
      dek,
      nonce,
      recipientPublicKey,
      senderSecretKey,
    );

    if (!ciphertext) {
      throw new Error("Failed to encrypt DEK");
    }

    // Return nonce + ciphertext
    const result = new Uint8Array(nonce.length + ciphertext.length);
    result.set(nonce);
    result.set(ciphertext, nonce.length);
    return result;
  },

  /**
   * Decrypt a wrapped DEK using my secret key and sender's public key
   *
   * @param {Uint8Array} wrappedDEK - Nonce + ciphertext from wrapDEK
   * @param {Uint8Array} senderPublicKey - Sender's X25519 public key
   * @param {Uint8Array} recipientSecretKey - My X25519 secret key
   * @returns {Promise<Uint8Array>} - The decrypted DEK
   */
  async unwrapDEK(wrappedDEK, senderPublicKey, recipientSecretKey) {
    const nacl = await loadNacl();

    // Extract nonce and ciphertext
    const nonce = wrappedDEK.slice(0, 24);
    const ciphertext = wrappedDEK.slice(24);

    // Decrypt using nacl.box.open
    const dek = nacl.box.open(
      ciphertext,
      nonce,
      senderPublicKey,
      recipientSecretKey,
    );

    if (!dek) {
      throw new Error("Failed to decrypt DEK - authentication failed");
    }

    return dek;
  },

  /**
   * Encrypt a DEK for a recipient using only their public key (anonymous sender)
   * Uses nacl.box.before for ephemeral key exchange
   *
   * @param {Uint8Array} dek - The document encryption key to share
   * @param {Uint8Array} recipientPublicKey - Recipient's X25519 public key
   * @returns {Promise<{wrapped: Uint8Array, ephemeralPublicKey: Uint8Array}>}
   */
  async wrapDEKAnonymous(dek, recipientPublicKey) {
    const nacl = await loadNacl();

    // Generate ephemeral keypair
    const ephemeral = nacl.box.keyPair();

    // Generate random nonce
    const nonce = nacl.randomBytes(24);

    // Encrypt using nacl.box
    const ciphertext = nacl.box(
      dek,
      nonce,
      recipientPublicKey,
      ephemeral.secretKey,
    );

    if (!ciphertext) {
      throw new Error("Failed to encrypt DEK");
    }

    // Return ephemeral public key + nonce + ciphertext
    const wrapped = new Uint8Array(32 + 24 + ciphertext.length);
    wrapped.set(ephemeral.publicKey);
    wrapped.set(nonce, 32);
    wrapped.set(ciphertext, 32 + 24);

    // H-3: Zero ephemeral secret key after use to reduce exposure window in memory
    ephemeral.secretKey.fill(0);

    return wrapped;
  },

  /**
   * Decrypt a DEK that was wrapped anonymously
   *
   * @param {Uint8Array} wrapped - ephemeralPubKey + nonce + ciphertext
   * @param {Uint8Array} recipientSecretKey - My X25519 secret key
   * @returns {Promise<Uint8Array>} - The decrypted DEK
   */
  async unwrapDEKAnonymous(wrapped, recipientSecretKey) {
    const nacl = await loadNacl();

    // Extract components
    const ephemeralPublicKey = wrapped.slice(0, 32);
    const nonce = wrapped.slice(32, 56);
    const ciphertext = wrapped.slice(56);

    // Decrypt using nacl.box.open
    const dek = nacl.box.open(
      ciphertext,
      nonce,
      ephemeralPublicKey,
      recipientSecretKey,
    );

    if (!dek) {
      throw new Error("Failed to decrypt DEK - authentication failed");
    }

    return dek;
  },

  /**
   * Generate a random DEK for a shared document
   * @returns {Uint8Array} - 32-byte random key
   */
  generateDEK() {
    return crypto.getRandomValues(new Uint8Array(32));
  },

  // Utility functions for base64 encoding/decoding
  // L-5: Chunked encoding to avoid stack overflow on large arrays
  toBase64(bytes) {
    let binary = "";
    const chunk = 32768;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
    }
    return btoa(binary);
  },

  fromBase64(base64) {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  },
};

/**
 * Manages the user's sharing keypair, storing it encrypted in the vault
 */
export class KeypairManager {
  constructor(vaultStorage) {
    this.vaultStorage = vaultStorage;
    this.keypair = null;
    this.serverSynced = false;
    // { local, published } (base64 public keys) when the vault keypair is not
    // the one the server publishes for this account — see identity repair.
    this.identityMismatch = null;
  }

  /**
   * The public key the server publishes for this account: a base64 string,
   * null when none is registered, undefined when the server couldn't be asked.
   */
  async fetchPublishedKey() {
    try {
      const response = await fetch("/api/keypair", { credentials: "include" });
      if (response.status === 404) return null;
      if (!response.ok) return undefined;
      return (await response.json()).public_key || null;
    } catch {
      return undefined;
    }
  }

  /**
   * Initialize or load the user's sharing keypair.
   *
   * A keypair is only ever generated for an account the server has no key
   * for. If the server already publishes one but this device's vault doesn't
   * hold it yet (vault not synced here, stale local copy), generating one
   * would write a new identity into the vault, sync it to every device and
   * replace the only secret that opens what was shared with this account —
   * exactly how identities diverged before. Refuse instead; the caller
   * retries once the vault has synced.
   */
  async initialize() {
    this.identityMismatch = null;
    const stored = await this.vaultStorage.getKeypair();
    const published = await this.fetchPublishedKey();

    if (stored) {
      this.keypair = {
        publicKey: Keypair.fromBase64(stored.publicKey),
        secretKey: Keypair.fromBase64(stored.secretKey),
      };
      this.serverSynced = stored.serverSynced || false;
      if (published === null) {
        await this.syncToServer();
      } else if (published && published !== stored.publicKey) {
        this.identityMismatch = { local: stored.publicKey, published };
        console.warn("[Keypair] Vault keypair differs from the published key — identity repair needed");
      } else if (published && !this.serverSynced) {
        await this.markSynced();
      }
      return this.keypair;
    }

    if (published !== null) {
      const error = new Error(published === undefined
        ? t("account.keypair.checkFailed")
        : t("account.keypair.notOnDevice"));
      error.code = "identity_unavailable";
      throw error;
    }

    // New account: generate, store in the vault, publish.
    this.keypair = await Keypair.generate();
    await this.vaultStorage.setKeypair({
      publicKey: Keypair.toBase64(this.keypair.publicKey),
      secretKey: Keypair.toBase64(this.keypair.secretKey),
      serverSynced: false,
    });
    await this.syncToServer();
    return this.keypair;
  }

  async markSynced() {
    this.serverSynced = true;
    await this.vaultStorage.setKeypair({
      publicKey: Keypair.toBase64(this.keypair.publicKey),
      secretKey: Keypair.toBase64(this.keypair.secretKey),
      serverSynced: true,
    });
  }

  /**
   * Sync public key to server
   */
  async syncToServer() {
    if (!this.keypair) {
      throw new Error("Keypair not initialized");
    }

    try {
      const response = await fetch("/api/keypair", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        credentials: "include",
        body: JSON.stringify({
          public_key: Keypair.toBase64(this.keypair.publicKey),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to sync keypair");
      }

      await this.markSynced();

      console.log("[Keypair] Public key synced to server");
    } catch (error) {
      console.error("[Keypair] Failed to sync to server:", error);
      throw error;
    }
  }

  /**
   * Zero the secret key material and clear the keypair reference.
   * Call this on logout to prevent key material from lingering in memory.
   */
  destroy() {
    if (this.keypair?.secretKey instanceof Uint8Array) {
      this.keypair.secretKey.fill(0);
    }
    this.keypair = null;
    this.serverSynced = false;
    this.identityMismatch = null;
  }

  /**
   * Get another user's public key by username
   * @param {string} username
   * @returns {Promise<Uint8Array>}
   */
  async getUserPublicKey(username) {
    const response = await fetch(
      `/api/users/${encodeURIComponent(username)}/pubkey`,
      {
        credentials: "include",
      },
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error(t("account.keypair.userNotFound", { username }));
      }
      const error = await response.json();
      throw new Error(error.error || "Failed to get user public key");
    }

    const data = await response.json();
    return Keypair.fromBase64(data.public_key);
  }

  /**
   * Wrap a DEK for sharing with another user
   * @param {Uint8Array} dek - The DEK to wrap
   * @param {string} username - Recipient's username
   * @returns {Promise<string>} - Base64 encoded wrapped DEK
   */
  async wrapDEKForUser(dek, username) {
    if (!this.keypair) {
      throw new Error("Keypair not initialized");
    }

    const recipientPubKey = await this.getUserPublicKey(username);
    const wrapped = await Keypair.wrapDEK(
      dek,
      recipientPubKey,
      this.keypair.secretKey,
    );
    return Keypair.toBase64(wrapped);
  }

  /**
   * Unwrap a DEK that was shared with me
   * @param {string} wrappedDEKBase64 - Base64 encoded wrapped DEK
   * @param {Uint8Array} senderPublicKey - Sender's public key (or use anonymous unwrap)
   * @returns {Promise<Uint8Array>} - The unwrapped DEK
   */
  async unwrapDEK(wrappedDEKBase64, senderPublicKey = null) {
    if (!this.keypair) {
      throw new Error("Keypair not initialized");
    }

    const wrapped = Keypair.fromBase64(wrappedDEKBase64);

    if (senderPublicKey) {
      return await Keypair.unwrapDEK(
        wrapped,
        senderPublicKey,
        this.keypair.secretKey,
      );
    } else {
      // Use anonymous unwrap (ephemeral sender key embedded in wrapped data)
      return await Keypair.unwrapDEKAnonymous(wrapped, this.keypair.secretKey);
    }
  }

  /**
   * Get my public key as base64
   */
  getPublicKeyBase64() {
    if (!this.keypair) {
      throw new Error("Keypair not initialized");
    }
    return Keypair.toBase64(this.keypair.publicKey);
  }
}
